import { useEffect, useRef, useState } from 'react';
import { loadRange, paginate } from './dateRange';
import { countBy } from './summaryStrip';

// Period filter (7 / 15 / 30 / 90 days / all time) for the master lists.
//   const { range, setRange, cache } = useRange(() => getItems(1));   // reloads the list when the period changes
//   const res = await fetchRanged({ apiService, endpoint, params, range, cache, pageNo, pageSize });
// For a period the list API is read page by page (newest first), the rows created inside the period are kept and
// paged here, because the list endpoints have no date filter. When the rows carry no created date at all the
// period cannot be applied, so the normal server page is returned instead.

const DATE_KEYS = ['createdOn', 'created_On', 'createdDate', 'createdDt', 'entryDate', 'modifiedOn', 'modify_On'];
const dateOf = (row) => {
  for (const k of DATE_KEYS) {
    const v = row?.[k];
    if (v && String(v) !== '0' && !String(v).startsWith('1900') && !String(v).startsWith('0001')) return v;
  }
  return '';
};

export const useRange = (reload, initial = 'all') => {
  const [range, setRange] = useState(initial);
  const cache = useRef({ key: '', rows: [], noDate: false });
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    cache.current.key = '';
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);
  return { range, setRange, cache };
};

export const fetchRanged = async ({
  apiService, endpoint, params, range, cache, pageNo = 1, pageSize = 20,
}) => {
  const plain = () => apiService.getApiParamater(endpoint, `?${params.toString()}`);
  if (range === 'all' || cache.current.noDate) return plain();

  const base = new URLSearchParams(params);
  base.delete('PageNo');
  base.delete('PageSize');
  const key = `${range}|${endpoint}|${base.toString()}`;
  let rows = cache.current.key === key ? cache.current.rows : null;
  if (!rows) {
    let sawDate = false;
    rows = await loadRange({
      range,
      dateKey: '__date',
      pageSize: 100,
      fetchPage: async (p, size) => {
        const q = new URLSearchParams(base);
        q.set('PageNo', p);
        q.set('PageSize', size);
        const list = (await apiService.getApiParamater(endpoint, `?${q.toString()}`))?.data?.result || [];
        return list.map((r) => {
          const d = dateOf(r);
          if (d) sawDate = true;
          return { ...r, __date: d };
        });
      },
    });
    if (!sawDate) {
      // No created date on these rows: fall back to the normal server paging.
      cache.current = { key: '', rows: [], noDate: true };
      return plain();
    }
    rows = rows.map(({ __date, ...r }) => r);
    cache.current = { key, rows, noDate: false };
  }
  const page = paginate(rows, pageNo || 1, pageSize || 20);
  return { data: { result: page.list, paginationData: page.pagination } };
};

// Client-side period filter for lists that return every record at once (Agent Master).
export const filterByRange = (rows, range) => {
  if (range === 'all') return rows;
  const dated = rows.map((r) => ({ r, d: dateOf(r) }));
  if (!dated.some((x) => x.d)) return rows;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - Number(range));
  return dated.filter((x) => x.d && new Date(x.d) >= cutoff).map((x) => x.r);
};

// Cards that filter the list. The card numbers follow the chosen period: with a period they are counted over every
// row of the period (cache.rows), otherwise over the rows on the page. Clicking a card filters the list to it
// (clicking again, or the first card, clears it). `shownRows` is what the grid should display while a card is active.
export const useCardFilter = (rowData, range, cache) => {
  const [filter, setFilter] = useState(null);
  useEffect(() => { setFilter(null); }, [range]);
  const periodRows = range !== 'all' && cache?.current?.rows?.length ? cache.current.rows : null;
  const statsRows = periodRows || rowData;
  const toggle = (f) => setFilter((prev) => (prev?.id === f.id ? null : f));
  const shownRows = filter ? statsRows.filter(filter.test) : rowData;
  return { filter, setFilter, toggle, statsRows, shownRows, inPeriod: range !== 'all' };
};

// Tiles for the most common values of a field; `get` reads the value from a row when it is not a plain key.
export const valueTiles = (cf, key, n, { get, note } = {}) => {
  const read = (r) => String((get ? get(r) : r?.[key]) ?? '').trim();
  return countBy(cf.statsRows.map((r) => ({ [key]: read(r) })), key).slice(0, n).map((x) => {
    const id = `${key}:${x.value}`;
    return {
      label: x.value, value: x.count, note, active: cf.filter?.id === id, onClick: () => cf.toggle({ id, test: (r) => read(r) === x.value }),
    };
  });
};
